import React, { useEffect, useCallback, useRef } from "react";
import type { Pos, Board, PlacedEmitter, InventoryEmitter, ClueStatus } from "../types.ts";
import { posKey } from "../types.ts";
import { useCanvas } from "../hooks/use-canvas.ts";
import { drawGrid, hitTestBoard } from "../canvas/grid-renderer.ts";

interface GameBoardProps {
  board: Board;
  placedEmitters: PlacedEmitter[];
  inventory: InventoryEmitter[];
  heldEmitterId: string | null;
  clueStatuses: Map<string, ClueStatus> | null;
  phase: string;
  onSelectInventory: (emitterId: string) => void;
  onPlace: (pos: Pos) => void;
  onRotate: (pos: Pos) => void;
  onPickup: (pos: Pos) => void;
}

export function GameBoard({
  board,
  placedEmitters,
  inventory,
  heldEmitterId,
  clueStatuses,
  phase,
  onSelectInventory,
  onPlace,
  onRotate,
  onPickup,
}: GameBoardProps) {
  const canvasRef = useCanvas(
    (ctx, w, h) =>
      drawGrid(ctx, board, placedEmitters, inventory, heldEmitterId, clueStatuses, w, h),
    [board, placedEmitters, inventory, heldEmitterId, clueStatuses]
  );

  // Long-press state for touch pickup
  const longPressTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const longPressHandled = useRef(false);
  const touchStartPos = useRef<{ x: number; y: number } | null>(null);

  // Click handling
  const handleClick = useCallback(
    (e: React.MouseEvent<HTMLCanvasElement>) => {
      if (longPressHandled.current) {
        longPressHandled.current = false;
        return;
      }
      if (phase === "won") return;
      const canvas = e.currentTarget;
      const rect = canvas.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;

      const hit = hitTestBoard(board, inventory.length, rect.width, rect.height, x, y);
      if (!hit) return;

      if (hit.type === "inventory") {
        const item = inventory[hit.index];
        if (item) onSelectInventory(item.id);
        return;
      }

      // Board click
      const { pos } = hit;
      const emitterOnCell = placedEmitters.find(
        (em) => posKey(em.pos) === posKey(pos) && !em.fixed
      );

      if (heldEmitterId) {
        // Holding an emitter — try to place
        onPlace(pos);
      } else if (emitterOnCell) {
        // Click on existing non-fixed emitter — rotate
        onRotate(pos);
      }
    },
    [board, inventory, placedEmitters, heldEmitterId, phase, onSelectInventory, onPlace, onRotate]
  );

  // Right-click to pick up (desktop)
  const handleContextMenu = useCallback(
    (e: React.MouseEvent<HTMLCanvasElement>) => {
      e.preventDefault();
      if (phase === "won") return;
      const canvas = e.currentTarget;
      const rect = canvas.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;

      const hit = hitTestBoard(board, inventory.length, rect.width, rect.height, x, y);
      if (!hit || hit.type !== "board") return;

      onPickup(hit.pos);
    },
    [board, inventory.length, phase, onPickup]
  );

  // Touch: long-press to pick up (mobile)
  const handleTouchStart = useCallback(
    (e: React.TouchEvent<HTMLCanvasElement>) => {
      if (phase === "won") return;
      const touch = e.touches[0];
      if (!touch) return;

      touchStartPos.current = { x: touch.clientX, y: touch.clientY };
      longPressHandled.current = false;

      longPressTimer.current = setTimeout(() => {
        const canvas = canvasRef.current;
        if (!canvas || !touchStartPos.current) return;

        const rect = canvas.getBoundingClientRect();
        const x = touchStartPos.current.x - rect.left;
        const y = touchStartPos.current.y - rect.top;

        const hit = hitTestBoard(board, inventory.length, rect.width, rect.height, x, y);
        if (hit && hit.type === "board") {
          const emitterOnCell = placedEmitters.find(
            (em) => posKey(em.pos) === posKey(hit.pos) && !em.fixed
          );
          if (emitterOnCell) {
            onPickup(hit.pos);
            longPressHandled.current = true;
          }
        }
      }, 500);
    },
    [board, inventory.length, placedEmitters, phase, onPickup]
  );

  const handleTouchMove = useCallback(
    (e: React.TouchEvent<HTMLCanvasElement>) => {
      if (!touchStartPos.current || !longPressTimer.current) return;
      const touch = e.touches[0];
      if (!touch) return;

      const dx = touch.clientX - touchStartPos.current.x;
      const dy = touch.clientY - touchStartPos.current.y;
      if (dx * dx + dy * dy > 100) {
        clearTimeout(longPressTimer.current);
        longPressTimer.current = null;
      }
    },
    []
  );

  const handleTouchEnd = useCallback(
    (e: React.TouchEvent<HTMLCanvasElement>) => {
      if (longPressTimer.current) {
        clearTimeout(longPressTimer.current);
        longPressTimer.current = null;
      }
      if (longPressHandled.current) {
        e.preventDefault();
      }
      touchStartPos.current = null;
    },
    []
  );

  useEffect(() => {
    return () => {
      if (longPressTimer.current) clearTimeout(longPressTimer.current);
    };
  }, []);

  // Keyboard: Escape to drop
  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (phase === "won") return;

      if (e.key === "Escape" && heldEmitterId) {
        // Drop held emitter back (deselect)
        onSelectInventory(heldEmitterId);
        return;
      }
    };
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [phase, heldEmitterId, onSelectInventory]);

  return (
    <canvas
      ref={canvasRef}
      className="w-full max-w-[520px] cursor-pointer touch-none"
      style={{ aspectRatio: "1 / 1.15" }}
      onClick={handleClick}
      onContextMenu={handleContextMenu}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
    />
  );
}
